import { Combobox, Transition } from "@headlessui/react";
import { IconDots, IconTrash } from "@tabler/icons-react";
import clsx from "clsx";
import Input from "components/Input";
import React, { Fragment } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import EditOptionPopup from "./EditOptionPopup";
import { on } from "events";

export type Option = {
  id: string;
  name: string;
};

const DropdownList = (props: {
  containerClassName: string;
  options: Option[];
  disabled?: boolean;
  selectedItems?: string[];
  onChangeItems?: (items: Option) => void;
  onRemoveOption?: (item: Option) => void;
  placeholder?: string;
  children?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>("");
  const optionsListRef = React.useRef<HTMLUListElement>(null);
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [optionsOpen, setOptionsOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [activeButtonFocus, setActiveButtonFocus] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [coords, setCoords] = React.useState<{
    x: number;
    y: number;
    id: string;
  } | null>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    optionsListRef.current?.scrollTo({ top: 0 });
    setFocusedIndex(-1);
  }, [searchTerm]);

  React.useEffect(() => {
    const maxWidth = activeButtonFocus ? "max-width: 65px" : "max-width: 60px";
    const timeout = setTimeout(() => {
      if (inputRef.current) inputRef.current.setAttribute("style", maxWidth);
      clearTimeout(timeout);
    }, 100);
  }, [activeButtonFocus]);

  const onClickOption = (item: Option) => {
    setSearchTerm("");
    props.onChangeItems?.(item);
  };

  const onEditOption = (e: React.MouseEvent, item: Option) => {
    e.stopPropagation();
    setOpenEdit(true);
    setCoords({ x: e.clientX, y: e.clientY + 10, id: item.id });
  };

  const handleRemoveOption = (e: React.MouseEvent, item: Option) => {
    e.stopPropagation();
    props.onRemoveOption?.(item);
  };

  function onArrowDown() {
    setFocusedIndex((prev) =>
      prev === props.options.length - 1 ? -1 : prev + 1
    );
  }

  function onArrowUp() {
    setFocusedIndex((prev) => (prev <= 0 ? -1 : Number(prev) - 1));
  }

  function focusToOptionsList() {
    setTimeout(() => {
      optionsListRef.current?.focus();
      optionsListRef.current?.scrollTo({ top: 0 });
    }, 1);
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    setActiveButtonFocus(true);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      !optionsOpen && buttonRef.current?.click();
      focusToOptionsList();
      onArrowDown();
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      focusToOptionsList();
      onArrowUp();
      return;
    }

    if (e.key === "Enter") {
      searchTerm && focusedIndex === -1
        ? onClickOption({
            id: "search_term",
            name: searchTerm,
          })
        : onClickOption(props.options[focusedIndex]);
      return;
    }
    setFocusedIndex(-1);
  };

  const onBlurInput = () => {
    setFocusedIndex(-1);
    setActiveButtonFocus(false);
    const timer = setTimeout(() => {
      setSearchTerm("");
      clearTimeout(timer);
    }, 200);
  };

  return (
    <div className={clsx(props.containerClassName, "rounded-md")}>
      <Combobox>
        <Combobox.Button
          className={clsx("w-full h-full pb-0 !mb-0 min-h-full", {
            "shadow-[inset_0_0_0_1px] !shadow-indigo-600 !border-none ring-0":
              activeButtonFocus || searchTerm,
          })}
          onClick={() => setActiveButtonFocus(true)}
          ref={buttonRef}
        >
          {({ open }) => {
            React.useEffect(() => {
              setOptionsOpen(open);
              !open && setOpenEdit(false);
            }, [open]);

            return (
              <div
                className={clsx(
                  "flex flex-wrap flex-grow gap-1 min-h-[36px] overflow-x-clip",
                  {
                    "py-[6px] pl-2 pr-2": props.selectedItems?.length,
                  }
                )}
              >
                {props.children}
                <Combobox.Input
                  as={Input}
                  value={searchTerm}
                  className={clsx(
                    "flex items-center !w-auto flex-1 !min-h-full border-none rounded-none overflow-hidden",
                    "bg-transparent min-w-auto pl-2 max-w-[65px] !py-0",
                    props.selectedItems?.length
                      ? "max-h-[25px]"
                      : "max-h-[36px]"
                  )}
                  containerClassName="!mb-0"
                  onChange={(e) => setSearchTerm(e as unknown as string)}
                  placeholder={props.placeholder}
                  onKeyDown={handleKeyDown}
                  onBlur={onBlurInput}
                  ref={inputRef}
                />
              </div>
            );
          }}
        </Combobox.Button>
        <Transition
          as={"div"}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options
            autoFocus
            className={clsx(
              "absolute z-10 my-1 w-75 overflow-visible",
              "max-h-[396px] overflow-auto rounded-md bg-white text-base !p-0",
              "shadow-lg border border-gray-300 focus:outline-none sm:text-sm",
              { "cursor-not-allowed": props.disabled }
            )}
            ref={optionsListRef}
          >
            <div
              className={clsx(
                "bg-gray-50 whitespace-nowrap w-full border-gray-300 text-xs font-medium py-1 px-4 text-gray-500",
                { "border-b border-b-gray-200": props.options.length }
              )}
            >
              {t("productEditor.selectOrCreateOption")}
            </div>
            {searchTerm && (
              <Combobox.Option
                key="search_term"
                id="search_term"
                value={searchTerm}
                className={clsx(
                  "flex !cursor-pointer group items-center text-gray-700 hover:bg-indigo-50 px-4 py-2 hover:text-indigo-700",
                  {
                    "bg-indigo-50 text-indigo-700":
                      focusedIndex === -1 && searchTerm,
                  }
                )}
                onClick={() =>
                  onClickOption({
                    id: "search_term",
                    name: searchTerm,
                  })
                }
              >
                <div className="flex justify-between gap-4 w-full">
                  <span className="whitespace-nowrap">{`“${searchTerm}” ${t(
                    "productEditor.create"
                  )}`}</span>
                  <span
                    className={clsx(
                      "whitespace-nowrap text-gray-500 text-sm font-normal",
                      { "text-indigo-500": focusedIndex === -1 }
                    )}
                  >
                    {t("commons.pressEnter")}
                  </span>
                </div>
              </Combobox.Option>
            )}
            {props.options.map((item, index) => (
              <Combobox.Option
                key={index}
                value={item}
                id={item.id}
                className={clsx(
                  "flex !cursor-pointer group items-center text-gray-700 hover:bg-indigo-50 px-4 py-2 hover:text-indigo-700",
                  "justify-between gap-6",
                  {
                    "bg-indigo-50 text-indigo-700": focusedIndex === index,
                  }
                )}
                onClick={() => onClickOption(item)}
              >
                <span>{item.name}</span>
                <IconTrash
                  size={20}
                  className="text-gray-700 hover:text-red-700 invisible group-hover:visible"
                  onClick={(e) => handleRemoveOption(e, item)}
                />
                {/* <IconDots size={24} onClick={(e) => onEditOption(e, item)} /> */}
                {/* {coords?.id === item.id && openEdit && (
                  <EditOptionPopup
                    item={item}
                    x={coords!.x}
                    y={coords!.y}
                    ref={popupRef}
                  />
                )} */}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
};

export default DropdownList;
